import React, { useState, useEffect, useRef } from 'react';
import { Globe, Check, ChevronDown } from 'lucide-react';
import { useLanguage } from '@/context/LanguageContext';

// Supported languages for the first release - matching LanguageContext
const supportedLanguages = [
  { code: 'en', name: 'English', flag: '🇺🇸' },
  { code: 'hi', name: 'हिंदी', flag: '🇮🇳' },
  { code: 'pa', name: 'ਪੰਜਾਬੀ', flag: '🇮🇳' },
  { code: 'bn', name: 'বাংলা', flag: '🇧🇩' },
  { code: 'te', name: 'తెలుగు', flag: '🇮🇳' },
  { code: 'ta', name: 'தமிழ்', flag: '🇮🇳' },
  { code: 'ml', name: 'മലയാളം', flag: '🇮🇳' },
  { code: 'kn', name: 'ಕನ್ನಡ', flag: '🇮🇳' },
  { code: 'gu', name: 'ગુજરાતી', flag: '🇮🇳' },
  { code: 'mr', name: 'मराठी', flag: '🇮🇳' }
];

const GoogleTranslateWidget: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const { currentLanguage, setLanguage, isLoading } = useLanguage();
  const dropdownRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);

  // Close dropdown when clicking outside or pressing Escape
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        buttonRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        !buttonRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isOpen) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('keydown', handleEscape);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen]);

  // Close dropdown when language changes
  useEffect(() => {
    setIsOpen(false);
  }, [currentLanguage]);

  const handleLanguageChange = (languageCode: string) => {
    setLanguage(languageCode);
    setIsOpen(false);
    
    // Show success message
    console.log(`Language changed to: ${languageCode}`);
  };

  const currentLanguageData = supportedLanguages.find(lang => lang.code === currentLanguage) || supportedLanguages[0];

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Language Selector Button */}
      <button
        ref={buttonRef}
        onClick={(e) => {
          e.stopPropagation();
          setIsOpen(!isOpen);
        }}
        disabled={isLoading}
        className={`flex items-center justify-between gap-2 px-3 transition-all duration-200 ${
          isLoading ? 'opacity-50 cursor-not-allowed' : 'hover:bg-brand-yellow/20 cursor-pointer'
        } ${isOpen ? 'bg-brand-yellow/30' : ''}`}
        style={{
          width: '127px',
          height: '35px',
          background: '#FFF8EB',
          borderRadius: '8px',
          fontFamily: "'Manrope', sans-serif",
          fontSize: '14px',
          lineHeight: '22px',
          fontWeight: '400',
          color: '#966400'
        }}
        aria-label="Select language"
        aria-expanded={isOpen}
        aria-haspopup="true"
      >
        <div className="flex items-center gap-1.5 flex-1 min-w-0">
          <Globe className="w-4 h-4 text-gray-500 flex-shrink-0" />
          <span className="text-lg flex-shrink-0">{currentLanguageData.flag}</span>
          <span className="truncate">{currentLanguageData.name}</span>
        </div>
        {isLoading ? (
          <div className="w-4 h-4 border-2 border-gray-300 border-t-brand-blue rounded-full animate-spin flex-shrink-0"></div>
        ) : (
          <ChevronDown className={`w-4 h-4 flex-shrink-0 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} />
        )}
      </button>

      {/* Language Dropdown */}
      {isOpen && (
        <div 
          className="absolute right-0 mt-2 w-64 bg-white border border-gray-200 rounded-lg shadow-xl z-[100] max-h-80 overflow-y-auto"
          role="menu"
          aria-orientation="vertical"
        >
          <div className="p-2">
            {supportedLanguages.map((language) => (
              <button
                key={language.code}
                onClick={(e) => {
                  e.stopPropagation();
                  handleLanguageChange(language.code);
                }}
                role="menuitem"
                className={`w-full flex items-center justify-between px-3 py-2.5 text-left text-sm rounded-md transition-colors ${
                  currentLanguage === language.code
                    ? 'bg-brand-blue text-white'
                    : 'text-gray-700 hover:bg-gray-100'
                }`}
              >
                <div className="flex items-center space-x-3">
                  <span className="text-lg">{language.flag}</span>
                  <span className="font-medium">{language.name}</span>
                </div>
                {currentLanguage === language.code && (
                  <Check className="w-4 h-4" />
                )}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default GoogleTranslateWidget;
